/**
 * Month-end P&L report trigger for NTS Owned-in-Lieu vehicles.
 *
 * Lets Admin / Dispatcher / Billing pick a month and open the printable
 * PDF in a new tab. The backend builds the PDF on demand from
 * `released_to_nts_at` falling inside the NZ calendar month — no
 * intermediate state to keep in sync.
 */
import { useState, useMemo } from "react";
import { FileText } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "./ui/dialog";
import { ntsPlReportUrl } from "../lib/api";
import { toast } from "sonner";
import { salvageShort, salvageFull } from "../lib/company";

function lastNMonths(n) {
  const out = [];
  const d = new Date();
  for (let i = 0; i < n; i++) {
    const m = new Date(d.getFullYear(), d.getMonth() - i, 1);
    const ym = `${m.getFullYear()}-${String(m.getMonth() + 1).padStart(2, "0")}`;
    const label = m.toLocaleString("default", { month: "long", year: "numeric" });
    out.push({ value: ym, label });
  }
  return out;
}

export default function NtsMonthlyPlDialog({ open, onOpenChange }) {
  const months = useMemo(() => lastNMonths(13), []);
  const [month, setMonth] = useState(months[0]?.value);

  const onOpenReport = () => {
    if (!month) {
      toast.error("Pick a month first");
      return;
    }
    const url = ntsPlReportUrl(month);
    window.open(url, "_blank", "noopener");
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="nts-pl-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black flex items-center gap-2">
            <FileText className="h-5 w-5 text-amber-600" />
            Month-end P&amp;L Report
          </DialogTitle>
          <DialogDescription>
            Printable PDF summarising vehicles signed over to {salvageShort()} in lieu of
            payment for the selected calendar month — write-off vs proceeds,
            disposed vs pending. Opens in a new tab.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-2 py-2">
          <label className="text-xs font-bold uppercase tracking-wider text-slate-600 dark:text-slate-300">
            Reporting Month
          </label>
          <select
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            className="w-full bg-white dark:bg-slate-800 dark:text-slate-100 border border-slate-300 dark:border-slate-700 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-amber-500"
            data-testid="nts-pl-month-select"
          >
            {months.map((m) => (
              <option key={m.value} value={m.value}>{m.label}</option>
            ))}
          </select>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Includes both Police Impounds and {salvageShort()} Tow jobs signed over during the month.
          </p>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="nts-pl-cancel-btn">
            Cancel
          </Button>
          <Button
            onClick={onOpenReport}
            className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white"
            data-testid="nts-pl-open-btn"
          >
            <FileText className="h-4 w-4.5" /> Open PDF
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
